<script setup lang="ts">
import { reactive } from "vue";

import SlideOver from "@/components/SlideOver.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";

import useHasPermissionUser from "@/composables/useHasPermissionUser";

import { FiltersForm } from "@/internal/modules/providers/composables/useSearchHotels";
import useStatusHotel from "@/internal/modules/providers/composables/useStatusHotel";

import useSearchChains from "../../chain/composables/useSearchChains";
import useSearchHotelGroups from "../../group/composables/useSearchHotelGroups";

interface Props {
  when: boolean;
  filters: FiltersForm;
}

interface Emits {
  (e: "close-slide-over", value: boolean): boolean;
  (e: "save-slide-over"): void;
  (e: "set-query-hotel", q: string): void;
}

const props = defineProps<Props>();

const emits = defineEmits<Emits>();

const filters = reactive<FiltersForm>(props.filters);

const { hasPermissionUser } = useHasPermissionUser();

const {
  data: chains,
  isLoading: loadingChain,
  setQuery: setQueryChains,
} = useSearchChains({
  immediate: true,
  queryKey: "list-chains",
  errorHandling: false,
});

const {
  data: hotelGroups,
  isLoading: loadingHotelGroups,
  setQuery: setQueryHotelGroups,
} = useSearchHotelGroups({
  immediate: true,
  queryKey: "hotel-groups",
  errorHandling: false,
});

const { data: status } = useStatusHotel({
  immediate: true,
});

const orderBy = [
  { value: "name", label: "Nombre" },
  { value: "created_at", label: "Fecha de creación" },
];

const orderDirectionOptions = [
  { value: "asc", label: "Ascendente" },
  { value: "desc", label: "Descendente" },
];

const hasCodeOptions = [
  { value: 1, label: "Con código de Amadeus" },
  { value: 0, label: "Sin código de Amadeus" },
  { value: "Todos", label: "Todos" },
];

const reset = () => {
  filters.q = "";
  filters.brand = undefined;
  filters.group = undefined;
  filters.chain = undefined;
  filters.hasCode = undefined;
  filters.status = undefined;
};
</script>

<template>
  <SlideOver
    :when="props.when"
    title="Filtros de hoteles"
    @update:close-slide-over="emits('close-slide-over', false)"
    @update:save-slide-over="emits('save-slide-over')"
    :hide-buttons="true"
  >
    <template #default>
      <div class="flex h-full flex-col bg-gray-50">
        <!-- Header -->
        <div class="border-b border-gray-200 bg-white px-5 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
                <span class="material-symbols-outlined text-xl text-amber-600">filter_alt</span>
              </div>
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Filtros</h2>
                <p class="text-xs text-gray-500">Personaliza tu búsqueda</p>
              </div>
            </div>
            <button
              @click="reset"
              v-tooltip="'Limpiar filtros'"
              class="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100"
            >
              <span class="material-symbols-outlined text-lg">mop</span>
              Limpiar
            </button>
          </div>
        </div>

        <!-- Filter sections -->
        <div class="flex-1 overflow-y-auto p-5 space-y-6">
          <!-- Search section -->
          <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div class="mb-3 flex items-center gap-2">
              <span class="material-symbols-outlined text-lg text-gray-400">search</span>
              <h3 class="text-sm font-semibold text-gray-700">Búsqueda</h3>
            </div>
            <StyledInput
              label="Nombre del hotel"
              placeholder="Escribe para buscar..."
              :debounce-time="500"
              @input="(e: string) => emits('set-query-hotel', e)"
            />
          </div>

          <!-- Relations section -->
          <div
            v-if="
              hasPermissionUser('viewAny-internal', 'App\\Models\\Chain') ||
              hasPermissionUser('viewAny-internal', 'App\\Models\\Group')
            "
            class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div class="mb-3 flex items-center gap-2">
              <span class="material-symbols-outlined text-lg text-gray-400">account_tree</span>
              <h3 class="text-sm font-semibold text-gray-700">Relaciones</h3>
            </div>

            <div class="space-y-4">
              <!-- Chain filter -->
              <div v-if="hasPermissionUser('viewAny-internal', 'App\\Models\\Chain')">
                <StyledCombobox
                  v-model="filters.chain"
                  placeholder="Buscar cadena..."
                  :options="chains?.data || []"
                  :loading-options="loadingChain"
                  :on-input-change="setQueryChains"
                  :show-input="!filters.chain"
                  label="Cadena"
                >
                  <template #default="{ option }">
                    <div class="flex items-center gap-2">
                      <span class="material-symbols-outlined text-base text-blue-500">link</span>
                      <span class="uppercase">{{ option.name }}</span>
                    </div>
                  </template>

                  <template #input-overlap>
                    <template v-if="filters.chain">
                      <div class="flex items-center gap-2">
                        <span class="material-symbols-outlined text-base text-blue-500">link</span>
                        <span>{{ filters.chain.name }}</span>
                      </div>
                      <button
                        class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200"
                        @click="filters.chain = undefined"
                      >
                        <span class="material-symbols-outlined text-lg">close</span>
                      </button>
                    </template>
                  </template>
                </StyledCombobox>
              </div>

              <!-- Group filter -->
              <div v-if="hasPermissionUser('viewAny-internal', 'App\\Models\\Group')">
                <StyledCombobox
                  v-model="filters.group"
                  placeholder="Buscar grupo..."
                  :options="hotelGroups?.data || []"
                  :loading-options="loadingHotelGroups"
                  :on-input-change="setQueryHotelGroups"
                  :show-input="!filters.group"
                  label="Grupo"
                >
                  <template #default="{ option }">
                    <div class="flex items-center gap-2">
                      <span class="material-symbols-outlined text-base text-emerald-500">workspaces</span>
                      <span class="uppercase">{{ option.name }}</span>
                    </div>
                  </template>

                  <template #input-overlap>
                    <template v-if="filters.group">
                      <div class="flex items-center gap-2">
                        <span class="material-symbols-outlined text-base text-emerald-500">workspaces</span>
                        <span>{{ filters.group.name }}</span>
                      </div>
                      <button
                        class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200"
                        @click="filters.group = undefined"
                      >
                        <span class="material-symbols-outlined text-lg">close</span>
                      </button>
                    </template>
                  </template>
                </StyledCombobox>
              </div>
            </div>
          </div>

          <!-- Status section -->
          <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div class="mb-3 flex items-center gap-2">
              <span class="material-symbols-outlined text-lg text-gray-400">toggle_on</span>
              <h3 class="text-sm font-semibold text-gray-700">Estatus y código</h3>
            </div>

            <div class="space-y-4">
              <StyledSelect
                placeholder="Seleccionar estado"
                label="Estatus del hotel"
                v-model="filters.status"
                :options="[
                  ...(status?.map((s) => ({
                    value: s.id,
                    label: s.displayName,
                  })) || []),
                  {
                    value: undefined,
                    label: 'Todos',
                  },
                ]"
              />

              <StyledSelect
                placeholder="Seleccione una opción"
                label="Código de Amadeus"
                v-model="filters.hasCode"
                :options="hasCodeOptions"
              />
            </div>
          </div>

          <!-- Order section -->
          <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div class="mb-3 flex items-center gap-2">
              <span class="material-symbols-outlined text-lg text-gray-400">sort</span>
              <h3 class="text-sm font-semibold text-gray-700">Ordenamiento</h3>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <StyledSelect v-model="filters.orderBy" :options="orderBy" label="Ordenar por" />

              <StyledSelect
                v-model="filters.orderDirection"
                :options="orderDirectionOptions"
                label="Dirección"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center gap-3 border-t border-gray-200 bg-white p-4">
        <button
          type="button"
          class="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          @click="reset"
        >
          <span class="material-symbols-outlined text-lg">mop</span>
          Limpiar
        </button>
        <button
          type="button"
          class="flex flex-1 items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-amber-700"
          @click="emits('close-slide-over', false)"
        >
          <span class="material-symbols-outlined text-lg">check</span>
          Aplicar filtros
        </button>
      </div>
    </template>
  </SlideOver>
</template>
